import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import { useTween } from "@/hooks/useTween";
import type { MaxConnectSchema, TelegramConnectSchema } from "@/schemas/sections";
import { readStorage, writeStorage } from "@/utils/safe-storage";
import {
  ConnectQrCodes,
  type ConnectQrTarget,
  useDesktopViewport,
  useQrCodes,
} from "./ConnectQrCodes";
import { CheckBadge, TelegramIcon } from "./icons";

export const TG_POLL_MS = 3000;
export const TG_POLL_FAST_MS = 1500;
// 10 minutes while a tap is unconfirmed — the OAuth device-flow shape ("leave the
// tab, confirm elsewhere, come back"), which is what this flow actually is. The
// old 3-minute cap gave up while a slow GC webhook was still in flight.
export const TG_POLL_MAX_MS = 600000;

/** The bare MAX bot, for a visitor with no personal bind link (`MaxConnectSchema.href`
 *  default) — the MAX twin of the bare `t.me/lenacareer_bot` fallback (ADR-0234). */
export const MAX_BOT_FALLBACK = "https://max.ru/id100123575160_bot";

/** Read the correlation token from the redirect (`?t=`). It is minted on the
 *  opt_in at submit and echoed by the GC bot funnel to `/api/tg-connect`, so the
 *  page can poll `/api/tg-connect/status` for the REAL connection confirmation. */
export function readConnectToken(): string {
  if (typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get("t") ?? "";
  } catch {
    return "";
  }
}

async function saveBotConnectClick(provider: "telegram" | "max") {
  const token = readConnectToken().trim();
  if (!token) return false;
  try {
    const response = await fetch("/api/tg-connect/click", {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ token, provider }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

export interface ConnectGate {
  /** The connect is CONFIRMED by the backend (or restored from this tab). */
  connected: boolean;
  /** The user tapped connect; we are waiting on the confirmation. */
  waiting: boolean;
  /** Whether the rest of the page (onboarding, cards) may render. True when the
   *  gate is inactive, the connect is confirmed, the user tapped "skip", or they
   *  opened MAX (which we cannot yet confirm — see `completed`). */
  revealed: boolean;
  /** Whether the step may be PRESENTED as done — the progress bar's 100% and the
   *  «Готово» eyebrow. Strictly narrower than `revealed`: opening MAX unlocks the
   *  page but proves nothing, so it must not render a completion claim over an
   *  un-connected block. Splitting these is what keeps the bar honest. */
  completed: boolean;
  /** True once the escape-hatch link should be offered. */
  showSkip: boolean;
  skip: () => void;
  markTapped: () => void;
  /** The visitor opened the MAX bot: reveal the rest of the page so they are not
   *  trapped behind a gate nothing can currently close, without claiming done. */
  markMaxTapped: () => void;
  /** The visitor says they scanned a QR. A camera fires NO click on this page, so
   *  without this a desktop visitor could connect on their phone and still be held
   *  behind the gate forever — `showSkip` only ever arms after a tap. Same
   *  non-committal reveal as `markMaxTapped`: it opens the page, it never claims
   *  the connect happened. */
  markQrScanned: () => void;
  /** Deep link, personal native-bind hash applied when it has arrived. */
  href: string;
  /** True once the backend reports a MAX connect. */
  maxConnected: boolean;
  /** `href`, but ONLY once it carries the personal bind hash — empty while it is
   *  still the bare-bot fallback. A QR must never encode the bare bot: scanning it
   *  would connect the visitor to a NEW GC user instead of their own. */
  personalTelegramHref: string;
  /** The visitor's PERSONAL MAX bind link, server-derived from the same native
   *  hash. Empty until it arrives, and empty forever for a visitor who has no
   *  hash (no `?t=` token, extraction skipped/failed, or already connected).
   *  Empty means the MAX button degrades to the bare bot link and gets no QR —
   *  exactly like `personalTelegramHref` (ADR-0234). */
  maxHref: string;
  restored: boolean;
}

/** Owns the connect state for the whole section, because it GATES the rest of
 *  the page — the connect block alone can't hold it. Polls
 *  `/api/tg-connect/status` for the REAL confirmation (never optimistic on tap).
 *
 *  Gate semantics: the page starts CLOSED whenever an enabled connect block is
 *  configured, so the server-rendered HTML is already the single-CTA page and
 *  nothing flashes into view and back out. It opens when the backend confirms,
 *  when the user says they already connected, or when there is no `?t=` token to
 *  verify against (a direct visit — never leave that visitor at a dead end). */
export function useConnectGate(
  cfg: z.input<typeof TelegramConnectSchema> | undefined,
): ConnectGate {
  const active = !!cfg && cfg.enabled !== false;
  const [token, setToken] = useState("");
  const [connected, setConnected] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [skipped, setSkipped] = useState(false);
  // Set by an action we cannot verify (opening MAX, or reporting a QR scan). It
  // reveals the page WITHOUT feeding `completed` — see the return below.
  const [intentRevealed, setIntentRevealed] = useState(false);
  const [maxConnected, setMaxConnected] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  // No token means no way to verify — open the gate rather than trap the visitor.
  const [unverifiable, setUnverifiable] = useState(false);
  // A confirmed connect in this tab → show 100% straight away on return.
  const [restored, setRestored] = useState(false);
  // The user's personal native-bind hash (extracted async on submit; ~1-2s).
  const [nativeHash, setNativeHash] = useState("");
  // The server-composed personal MAX bind link. Derived from the SAME credential
  // as `nativeHash`, so it arrives and disappears with it.
  const [maxHref, setMaxHref] = useState("");
  const nativeHashRef = useRef("");
  nativeHashRef.current = nativeHash;

  useEffect(() => {
    if (!active) return;
    const tok = readConnectToken();
    setToken(tok);
    if (!tok) {
      setUnverifiable(true);
      return;
    }
    if (readStorage("session", `lc_tg_ok:${tok}`) === "1") {
      setConnected(true);
      setRestored(true);
    }
  }, [active]);

  // Offer the escape hatch only after the user has actually tapped connect and
  // the confirmation has had time to land — so it never competes with the CTA.
  useEffect(() => {
    if (!waiting || connected) return;
    const delay = Math.max(0, cfg?.skipAfterSeconds ?? 25) * 1000;
    const timer = window.setTimeout(() => setShowSkip(true), delay);
    return () => window.clearTimeout(timer);
  }, [waiting, connected, cfg?.skipAfterSeconds]);

  // One poll drives both: the personal native-bind hash for the deep link, and —
  // after the user taps — the GC-side connect confirmation that advances the bar
  // to 100%. Runs from mount to fetch the hash; keeps going while confirming a
  // tap. Re-polls on tab refocus (return from the Telegram app).
  useEffect(() => {
    if (!token || connected) return;
    let stop = false;
    const startedAt = Date.now();
    const poll = async () => {
      if (stop) return;
      try {
        const res = await fetch(`/api/tg-connect/status?t=${encodeURIComponent(token)}`, {
          headers: { accept: "application/json" },
        });
        if (res.ok) {
          const data = (await res.json()) as {
            connected?: boolean;
            maxConnected?: boolean;
            nativeHash?: string | null;
            maxUrl?: string | null;
          };
          // Mirror `/status`'s withholding: it drops the credential once EITHER
          // provider is connected, and the client must not keep a scannable
          // re-bind on screen after that.
          //
          // ⚠️ Clear only on that AUTHORITATIVE signal, never on a merely absent
          // field. `/status` fails OPEN — a DB hiccup answers 200 with the empty
          // shape so the client retries — so an unconditional clear would wipe a
          // perfectly good credential mid-session, dropping the visitor back to
          // the anonymous bare-bot link until the next poll.
          // Drop each provider's link when THAT provider is connected; otherwise
          // keep what we already hold. `/status` is unchanged and still withholds
          // both once either connects, so "keep" means "keep the copy this tab
          // already fetched" — never a fresh one.
          //
          // 🛑 This is what lets a visitor who just connected Telegram still be
          // offered MAX. It is safe because the client never renders anything
          // `/api/tg-connect/status` did not already send to THIS tab: the
          // server's withholding contract above is unchanged — it nulls BOTH
          // `nativeHash` and `maxUrl` the moment EITHER provider connects, keyed
          // on `tgConnectToken`, never on the recipient — so the set of tabs
          // that ever hold a credential is exactly what it was before this
          // change. What changes is only how long an already-delivered MAX link
          // stays on screen after a same-tab Telegram connect. Hiding an
          // already-delivered value from the UI was never itself a security
          // control — it was already in this tab's network response.
          // `inheritTgConnection` is a separate mechanism: it makes a REPEAT
          // submit of an already-connected address start out connected, so that
          // tab's very first poll returns nulls too — but that is one way a tab
          // can end up never having held a credential, not the reason retaining
          // an already-held one is safe, and it says nothing about a first-ever
          // MAX-only connect (ADR-0227).
          if (data?.connected) setNativeHash("");
          else if (data?.nativeHash) setNativeHash(data.nativeHash);
          if (data?.maxConnected) setMaxHref("");
          else if (data?.maxUrl) setMaxHref(data.maxUrl);
          if (data?.maxConnected) setMaxConnected(true);
          if (data?.connected) {
            setConnected(true);
            writeStorage("session", `lc_tg_ok:${token}`, "1");
            return;
          }
        }
      } catch {}
      // Keep polling while confirming a tap, or until the native hash arrives.
      const needMore =
        !stop && Date.now() - startedAt < TG_POLL_MAX_MS && (waiting || !nativeHashRef.current);
      if (needMore) window.setTimeout(poll, waiting ? TG_POLL_MS : TG_POLL_FAST_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    poll();
    return () => {
      stop = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, connected, waiting]);

  const base = cfg?.href ?? "https://t.me/lenacareer_bot";
  // Prefer the personal native-bind hash (binds Telegram to the EXACT GC user);
  // fall back to the bare bot (still connects, just not natively bound). The hash
  // is RAW on purpose — encoding/markdown must not touch the `__sN`.
  const href = nativeHash ? `${base}${base.includes("?") ? "&" : "?"}start=${nativeHash}` : base;

  // `completed` deliberately EXCLUDES `intentRevealed`: until a GC-side writer
  // marks `maxConnectedAt`, opening MAX (or scanning a code) tells us nothing, so
  // it may unlock the page but must not advance the bar or flip the eyebrow.
  const completed = !active || connected || maxConnected || skipped || unverifiable;
  return {
    connected,
    waiting,
    revealed: completed || intentRevealed,
    completed,
    showSkip: showSkip && !connected,
    skip: () => setSkipped(true),
    markTapped: () => setWaiting(true),
    markMaxTapped: () => setIntentRevealed(true),
    markQrScanned: () => {
      setIntentRevealed(true);
      // 🛑 Also arms the poll. `needMore` above stops polling once the hash has
      // arrived unless `waiting` is set, so without this a visitor who scanned
      // and completed the bind on their phone would NEVER be auto-confirmed —
      // the tab stops asking ~1.5 s after load and a desktop scanner never
      // leaves the tab, so even the visibilitychange re-poll does not fire.
      setWaiting(true);
    },
    href,
    maxConnected,
    personalTelegramHref: nativeHash ? href : "",
    maxHref,
    restored,
  };
}

/**
 * Which providers get a QR, if any.
 *
 * Desktop visitors cannot act on any button here — MAX's own web client logs in by
 * QR only — so both codes are offered BEFORE a provider is chosen.
 *
 * Each code encodes the PERSONAL bind link when the server has served one and the
 * bare bot link otherwise (ADR-0235) — the same href the button next to it carries,
 * so a desktop visitor without a personal hash (repeat address past the ADR-0105
 * cap, failed extraction, direct visit) still has a phone route to BOTH bots; for
 * MAX it is the only desktop route at all.
 *
 * 🛑 A provider's code is withheld once THAT provider is connected, for the same
 * reason `/status` stops serving the hash (ADR-0105/0223): a personal code IS the
 * bind credential, and a connected account must never be handed a scannable re-bind.
 */
export function resolveQrTargets({
  gate,
  isDesktop,
  showMax,
  maxHref,
}: {
  gate: ConnectGate;
  isDesktop: boolean;
  showMax: boolean;
  /** The resolved MAX href — personal when served, else the bare bot. */
  maxHref: string;
}): ConnectQrTarget[] {
  if (!isDesktop) return [];
  const targets: ConnectQrTarget[] = [];
  // Per provider, not all-or-nothing: a Telegram-connected desktop visitor must
  // still get the MAX code — on desktop it is their ONLY route to MAX, since
  // web.max.ru itself logs in by QR.
  if (!gate.connected && gate.href) {
    targets.push({
      provider: "telegram",
      href: gate.personalTelegramHref || gate.href,
      label: "Telegram",
    });
  }
  if (showMax && maxHref) {
    targets.push({ provider: "max", href: maxHref, label: "MAX" });
  }
  return targets;
}

/** `surface` inverts the block's WHOLE colour contract, not just its background.
 *  On the purple block every foreground is `brand-content` (white) and the CTAs
 *  are white pills with purple labels; on a white card the figure and the
 *  primary CTA become purple and the text becomes page ink. Flipping only the
 *  background would leave white CTAs on a white card — the failure mode this
 *  table exists to make impossible to reach by editing one class.
 *
 *  `text-brand-content` survives into `plain` in exactly one place: the primary
 *  CTA, which carries its own `bg-brand`. White on purple is correct there. */
const SURFACE_TOKENS = {
  plain: {
    card: "border border-border bg-bg shadow-sm",
    eyebrow: "text-muted",
    stat: "text-brand",
    track: "bg-brand/15",
    fill: "bg-brand",
    title: "text-fg",
    body: "text-fg-soft",
    soft: "text-muted",
    primaryCta: "bg-brand text-brand-content",
    secondaryCta: "bg-brand/10 text-brand",
    maxChip: "bg-brand/15",
  },
  brand: {
    card: "bg-brand text-brand-content shadow-lg",
    eyebrow: "text-brand-content/80",
    stat: "text-brand-content",
    track: "bg-brand-content/20",
    fill: "bg-brand-content",
    title: "text-brand-content",
    body: "text-brand-content/85",
    soft: "text-brand-content/70",
    primaryCta: "bg-brand-content text-brand",
    secondaryCta: "bg-brand-content text-brand",
    maxChip: "bg-brand/10",
  },
} as const;

/** The prominent connect module (the dreamjob-weekend «остался последний шаг /
 *  50%» pattern) — presentational; `useConnectGate` owns the state. */
export function TelegramConnectBlock({
  cfg,
  gate,
  maxConnect,
  onMaxTapped,
}: {
  cfg: z.input<typeof TelegramConnectSchema>;
  gate: ConnectGate;
  maxConnect?: z.input<typeof MaxConnectSchema>;
  onMaxTapped: () => void;
}) {
  const { connected, waiting, completed, restored, href } = gate;
  // MAX degrades exactly like Telegram (ADR-0234): the PERSONAL bind link when
  // `/status` served one, else the bare bot link from config. The same href feeds
  // the button and, on desktop, the QR code (ADR-0235).
  // Deliberately NOT `!connected`: someone who just connected Telegram is exactly
  // who we want to offer MAX to.
  const maxHref = gate.maxHref || maxConnect?.href || MAX_BOT_FALLBACK;
  const showMax = !!maxConnect && maxConnect.enabled !== false && !gate.maxConnected;

  const isDesktop = useDesktopViewport();
  const qrTargets = resolveQrTargets({ gate, isDesktop, showMax, maxHref });
  const qrCodes = useQrCodes(qrTargets);
  const start = cfg.progressStart ?? 50;
  const end = cfg.progressEnd ?? 100;
  // The bar tracks COMPLETION, not mere reveal: whenever the step is genuinely
  // finished — confirmed, skipped, or unverifiable — a 50% bar above unlocked
  // content would read as a broken promise. But an unconfirmable MAX tap only
  // reveals; claiming 100% there would be the opposite lie.
  const target = completed ? end : start;
  const progress = useTween(target, restored ? 0 : 900);

  // `completed` (not `connected`) drives the eyebrow for the same reason as the
  // bar. The check badge and «Готово! Проверьте Telegram» below stay tied to
  // `connected` — those assert a fact about Telegram we may not actually have.
  const eyebrow = completed
    ? "Готово"
    : waiting
      ? "Ожидаем подключение…"
      : (cfg.eyebrow ?? "Остался последний шаг");

  const plain = cfg.surface === "plain";
  const s = SURFACE_TOKENS[plain ? "plain" : "brand"];

  return (
    <div
      data-component="TelegramConnectBlock"
      data-surface={plain ? "light" : "dark"}
      className={clsx("mx-auto mt-6 max-w-xl rounded-3xl p-6 md:mt-8 md:p-8", s.card)}
    >
      <Flex align="center" justify="between" gap={8} className="mb-2">
        {/* `min-w-0` + `text-balance`: at 360px a long eyebrow («Ожидаем
            подключение…», or an authored one) wrapped to two lines and crowded
            the figure. It may still wrap — it just no longer does so raggedly
            against the number. */}
        <Text variant="eyebrow" as="p" className={clsx("min-w-0 text-balance", s.eyebrow)}>
          {eyebrow}
        </Text>
        {/* The figure is the block's focal point (design-system `stat` role — display
            face, solid leading), so it reads first, ahead of the page headline. */}
        <Text variant="stat" as="span" className={clsx(s.stat, "tabular-nums")}>
          {Math.round(progress)}%
        </Text>
      </Flex>
      <div
        className={clsx("h-2.5 w-full overflow-hidden rounded-full", s.track)}
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Прогресс регистрации"
      >
        <div className={clsx("h-full rounded-full", s.fill)} style={{ width: `${progress}%` }} />
      </div>

      {connected || gate.maxConnected ? (
        <Flex align="start" gap={8} className="mt-4">
          <CheckBadge plain={plain} />
          <div className="min-w-0 flex-1">
            <Text variant="subheading" as="p" className={s.title}>
              {connected
                ? (cfg.connectedTitle ?? "Готово! Проверьте Telegram")
                : "Готово! Проверьте MAX"}
            </Text>
            {connected && cfg.connectedDescription && (
              <Text variant="body-sm" as="p" className={clsx("mt-1", s.body)}>
                {cfg.connectedDescription}
              </Text>
            )}
            {showMax && (
              // Framed as an ADDITION, never as an unfinished step — this person
              // already succeeded, and the block above says so.
              <Text variant="body-sm" as="p" className={clsx("mt-2", s.body)}>
                Пользуетесь MAX? Там тоже можно получать материалы и анонсы.
              </Text>
            )}
            {!connected && gate.maxConnected && (
              // The mirror: MAX connected first, and the Telegram link is still
              // retained (useConnectGate/resolveQrTargets keep it), so offer it
              // here too, framed the same way — an addition, not a missed step.
              <Text variant="body-sm" as="p" className={clsx("mt-2", s.body)}>
                Пользуетесь Telegram? Там тоже можно получать материалы и анонсы.
              </Text>
            )}
          </div>
        </Flex>
      ) : (
        <div className="mt-4">
          <Text variant="subheading" as="h2" className={s.title}>
            {cfg.title}
          </Text>
          <Text variant="body-sm" as="p" className={clsx("mt-1", s.body)}>
            {cfg.description}
          </Text>
          {waiting && (
            <Text variant="body-sm" as="p" className={clsx("mt-2", s.soft)}>
              Открыли Telegram? Нажмите «Старт» — как подключитесь, отметим здесь автоматически.
            </Text>
          )}
        </div>
      )}

      <ConnectQrCodes codes={qrCodes} onScanned={gate.markQrScanned} plain={plain} />

      <Stack gap={8} className="mt-5">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            gate.markTapped();
            void saveBotConnectClick("telegram");
          }}
          data-track="lm_next.cta.telegram_connect"
          className={clsx(
            "group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 font-(weight:--fw-bold) no-underline shadow-md transition-transform duration-300 hover:-translate-y-0.5 md:px-6",
            s.primaryCta,
          )}
        >
          <TelegramIcon />
          {connected ? "Открыть Telegram" : (cfg.cta ?? "Подключить бота")}
        </a>
        {showMax && (
          <a
            href={maxHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              void saveBotConnectClick("max");
              onMaxTapped();
            }}
            data-track="lm_next.cta.max_connect"
            className={clsx(
              "group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 font-(weight:--fw-bold) no-underline shadow-md transition-transform duration-300 hover:-translate-y-0.5 md:px-6",
              s.secondaryCta,
            )}
          >
            <span
              aria-hidden="true"
              className={clsx(
                "rounded-lg px-1.5 py-1 text-(length:--type-caption) font-(weight:--fw-heavy)",
                s.maxChip,
              )}
            >
              MAX
            </span>
            {maxConnect?.label ?? "Подключить в MAX"}
          </a>
        )}
      </Stack>
      {cfg.note && (
        <Text variant="body-sm" as="p" className={clsx("mt-3 text-center", s.soft)}>
          {cfg.note}
        </Text>
      )}
      {showMax && maxConnect?.note && (
        <Text variant="body-sm" as="p" className={clsx("mt-3 text-center", s.soft)}>
          {maxConnect.note}
        </Text>
      )}
      {gate.showSkip && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={gate.skip}
            data-track="lm_next.cta.connect_skip"
            className={clsx(
              "text-(length:--type-body-sm) font-(weight:--fw-medium) underline",
              s.soft,
            )}
          >
            {cfg.skipLabel ?? "Я подключил(а) — показать остальное"}
          </button>
        </div>
      )}
    </div>
  );
}

/** One upsell card — a full-tap link. Horizontal compact row on mobile, vibrant
 *  vertical card on desktop. Tone falls back to the default order by position. */
